/**
 * v0.8.56 — counts cloud writes so a background refresh never lands on
 * top of an edit that's still saving (or that started while the refresh
 * was fetching). See `useRefreshOnFocus`.
 *
 * `trackRepoWrites` wraps a repository so every non-`get*` method call is
 * counted — one place covers every write path (payments, close month,
 * income edits, imports…) without touching each store action.
 */
let pending = 0
let epoch = 0

export function hasPendingWrites(): boolean {
  return pending > 0
}

/** Increments on every write start; a refresh compares before vs after. */
export function writeEpoch(): number {
  return epoch
}

async function counted<T>(run: () => Promise<T>): Promise<T> {
  pending++
  epoch++
  try {
    return await run()
  } finally {
    pending--
  }
}

export function trackRepoWrites<T extends object>(repo: T): T {
  return new Proxy(repo, {
    get(target, prop) {
      const value = Reflect.get(target, prop, target)
      if (typeof value !== 'function') return value
      const bound = (value as (...args: unknown[]) => unknown).bind(target)
      if (typeof prop === 'string' && prop.startsWith('get')) return bound
      return (...args: unknown[]) => counted(() => Promise.resolve(bound(...args)))
    },
  })
}

/** Test-only. */
export function __resetWriteTrackerForTests(): void {
  pending = 0
  epoch = 0
}
